import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import i18n from '@/utils/i18n'

import { dateFnsLocale } from '@/utils/dateLocale'
import { apiLogDirection, listExternalApiLog } from '@/api/external-api-log'
import { leadCardCls } from '@/components/inspections/leadDetail'

/**
 * Мини-блок «Запросы Avito по этому лиду». Показывает последние ~10 вызовов
 * `/avito-stats` с этим lead_id (из public.external_api_log). Нужен при
 * разборе инцидентов «Avito говорит — у вас не отвечает API».
 */
export function LeadAvitoApiCalls({ leadId }: { leadId: string }) {
  const { t } = useTranslation()
  const q = useQuery({
    queryKey: ['external-api-log', 'lead', leadId],
    queryFn: () => listExternalApiLog({ partner: 'avito', leadId, limit: 10 }),
    refetchInterval: 30_000,
    staleTime: 10_000,
  })
  const rows = q.data ?? []
  return (
    <section className={leadCardCls}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="stryzh-heading-card">{t('leads.detail.avitoReqTitle')}</h2>
        <Link
          to="/avito-api-log"
          className="text-xs text-[color:var(--stryzh-color-accent-orange)] hover:underline"
        >
          {t('leads.detail.fullLog')}
        </Link>
      </div>
      {q.isLoading ? (
        <div className="stryzh-text-muted">{t('leads.detail.loading')}</div>
      ) : rows.length === 0 ? (
        <p className="stryzh-text-muted">{t('leads.detail.avitoNoReq')}</p>
      ) : (
        <ol className="space-y-1.5 text-xs">
          {rows.map((r) => {
            const at = format(new Date(r.called_at), 'd MMM HH:mm:ss', { locale: dateFnsLocale() })
            const dir = apiLogDirection(r.endpoint)
            const dirShort = dir.kind === 'in' ? t('leads.detail.reqIn') : dir.kind === 'out' ? t('leads.detail.reqOut') : r.endpoint
            const dirColor =
              dir.kind === 'in'
                ? 'text-blue-300'
                : dir.kind === 'out'
                  ? 'text-purple-300'
                  : 'text-[color:var(--stryzh-color-text-secondary)]'
            const httpColor =
              r.status_code >= 500
                ? 'text-red-300'
                : r.status_code >= 400
                  ? 'text-yellow-300'
                  : 'text-green-300'
            return (
              <li
                key={r.id}
                className="flex flex-wrap items-baseline gap-x-2 rounded border border-[color:var(--stryzh-color-border-subtle)] bg-[color:var(--stryzh-color-void)] px-2 py-1.5"
              >
                <span className="font-[family-name:var(--stryzh-font-family-mono)] text-[color:var(--stryzh-color-text-secondary)]">
                  {at}
                </span>
                <span className={`font-[family-name:var(--stryzh-font-family-mono)] ${dirColor}`} title={r.endpoint}>
                  {dirShort}
                </span>
                <span className={`font-[family-name:var(--stryzh-font-family-mono)] font-semibold ${httpColor}`}>
                  {r.status_code}
                </span>
                <span className="font-[family-name:var(--stryzh-font-family-mono)] text-[color:var(--stryzh-color-text-secondary)]">
                  {r.reason ?? '—'}
                </span>
                <span className="ml-auto font-[family-name:var(--stryzh-font-family-mono)] text-[10px] text-[color:var(--stryzh-color-text-secondary)]">
                  {r.duration_ms != null ? `${r.duration_ms} ${i18n.language === 'en' ? 'ms' : 'мс'}` : ''}
                </span>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
